import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import "./X5S1.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

function X5S1(props) {
  const { x1200PxLogo_Icam__20081 } = props;
  const [hasImplemented, setHasImplemented] = useState(false);
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({
    // Part 1: Current state - 5S application today
    // 1. Seiri (Sort)
    identifiedUselessItems: "",
    criteriaForNecessity: "",
    actionPlanForElimination: "",
    
    // 2. Seiton (Set in order)
    definedLocations: "",
    organizationLogic: "",
    easyToFind: "",
    
    // 3. Seiso (Shine)
    regularCleaning: "",
    formalCleaningPlan: "",
    anomalyIdentification: "",
    
    // 4. Seiketsu (Standardize)
    visualStandards: "",
    documentedStandards: "",
    standardsRevisionFrequency: "",
    
    // 5. Shitsuke (Sustain)
    fiveSCulture: "",
    internalAudits: "",
    deviationManagement: "",
    
    // Part 2: Future desired state - Vision and objectives
    idealVision: "",
    objectives: "",
    extensionToOtherDepartments: "",
    expectedBehaviorChanges: "",
    digitalTools: "",
    
    // Part 3: Gap analysis, monitoring, and change management
    projectManager: "",
    performanceIndicators: "",
    mainDifficulties: "",
    trainingActions: "",
    bestPractices: ""
  });
  
  const [uploadStatus, setUploadStatus] = useState({
    isUploading: false,
    progress: 0,
    error: null,
    success: false,
    fileName: ""
  });
  const fileInputRef = useRef(null);
  const questionnaireRef = useRef(null);

  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // File type validation
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/svg+xml', 
                         'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                         'application/vnd.ms-excel',
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      setUploadStatus({
        ...uploadStatus,
        error: "Unsupported file type. Please use PDF, JPG, PNG, SVG, Excel, or Word.",
        success: false
      });
      return;
    }

    // File size validation (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setUploadStatus({
        ...uploadStatus,
        error: "File too large. Maximum size: 10MB",
        success: false
      });
      return;
    }

    try {
      setUploadStatus({
        isUploading: true,
        progress: 0,
        error: null,
        success: false,
        fileName: file.name
      });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('answers', JSON.stringify(answers));

      const xhr = new XMLHttpRequest();
      
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          setUploadStatus(prev => ({
            ...prev,
            progress
          }));
        }
      };

      const uploadPromise = new Promise((resolve, reject) => {
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.response));
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Upload failed'));
      });

      xhr.open('POST', '/api/5s/upload');
      xhr.send(formData);

      const response = await uploadPromise;

      setUploadStatus({
        isUploading: false,
        progress: 100,
        error: null,
        success: true,
        fileName: file.name,
        fileId: response.file_id
      });

    } catch (error) {
      setUploadStatus({
        isUploading: false,
        progress: 0,
        error: "An error occurred during upload. Please try again.",
        success: false,
        fileName: ""
      });
    }
  };

  const handleAnswerChange = (field, value) => {
    setAnswers({
      ...answers,
      [field]: value
    });
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    } else {
      setShowImport(true);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      if (questionnaireRef.current) {
        questionnaireRef.current.scrollTop = 0;
      }
    }
  };

  const handleYesClick = () => {
    setHasImplemented(true);
    setShowQuestionnaire(true);
  };

  // Progress indicator
  const renderProgressIndicator = () => {
    return (
      <div className="progress-indicator">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>1</div>
        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>2</div>
        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>3</div>
      </div>
    );
  };

  const renderQuestionnaire = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 1: Current State - How 5S is applied today</h2>
            <p className="step-description">
              The 5S are: Seiri (Sort), Seiton (Set in order), Seiso (Shine), Seiketsu (Standardize), Shitsuke (Sustain)
            </p>
            
            <div className="question-group">
              <h3 className="question-group-title">1. Seiri (Sort)</h3>
              
              <div className="question-item">
                <label htmlFor="identifiedUselessItems">
                  Have you identified unnecessary items at your workstations?
                </label>
                <textarea 
                  id="identifiedUselessItems" 
                  value={answers.identifiedUselessItems} 
                  onChange={(e) => handleAnswerChange('identifiedUselessItems', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="criteriaForNecessity">
                  What criteria do you use to decide whether a tool, document, or material is necessary?
                </label>
                <textarea 
                  id="criteriaForNecessity" 
                  value={answers.criteriaForNecessity} 
                  onChange={(e) => handleAnswerChange('criteriaForNecessity', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="actionPlanForElimination">
                  Is there an action plan to eliminate superfluous items?
                </label>
                <textarea 
                  id="actionPlanForElimination" 
                  value={answers.actionPlanForElimination} 
                  onChange={(e) => handleAnswerChange('actionPlanForElimination', e.target.value)}
                  rows="3"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">2. Seiton (Set in order)</h3>
              
              <div className="question-item">
                <label htmlFor="definedLocations">
                  Do tools, equipment, and documents have defined and labeled locations?
                </label>
                <textarea 
                  id="definedLocations" 
                  value={answers.definedLocations} 
                  onChange={(e) => handleAnswerChange('definedLocations', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="organizationLogic">
                  Have you implemented an organization system based on frequency of use?
                </label>
                <textarea 
                  id="organizationLogic" 
                  value={answers.organizationLogic} 
                  onChange={(e) => handleAnswerChange('organizationLogic', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="easyToFind">
                  Is it easy for a newcomer to find what they need without assistance?
                </label>
                <textarea 
                  id="easyToFind" 
                  value={answers.easyToFind} 
                  onChange={(e) => handleAnswerChange('easyToFind', e.target.value)}
                  rows="3"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">3. Seiso (Shine)</h3>
              
              <div className="question-item">
                <label htmlFor="regularCleaning">
                  Is workstation cleanliness ensured regularly?
                </label>
                <textarea 
                  id="regularCleaning" 
                  value={answers.regularCleaning} 
                  onChange={(e) => handleAnswerChange('regularCleaning', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="formalCleaningPlan">
                  Is there a formal cleaning plan assigned to each area/workstation?
                </label>
                <textarea 
                  id="formalCleaningPlan" 
                  value={answers.formalCleaningPlan} 
                  onChange={(e) => handleAnswerChange('formalCleaningPlan', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="anomalyIdentification">
                  Are anomalies or leaks systematically identified during cleaning?
                </label>
                <textarea 
                  id="anomalyIdentification" 
                  value={answers.anomalyIdentification} 
                  onChange={(e) => handleAnswerChange('anomalyIdentification', e.target.value)}
                  rows="3"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">4. Seiketsu (Standardize)</h3>
              
              <div className="question-item">
                <label htmlFor="visualStandards">
                  Do you have visual standards (signs, colors, floor markings) in the workshop or warehouse?
                </label>
                <textarea 
                  id="visualStandards" 
                  value={answers.visualStandards} 
                  onChange={(e) => handleAnswerChange('visualStandards', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="documentedStandards">
                  Are standards documented and shared with all concerned employees?
                </label>
                <textarea 
                  id="documentedStandards" 
                  value={answers.documentedStandards} 
                  onChange={(e) => handleAnswerChange('documentedStandards', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="standardsRevisionFrequency">
                  How often do you revise your 5S standards?
                </label>
                <textarea 
                  id="standardsRevisionFrequency" 
                  value={answers.standardsRevisionFrequency} 
                  onChange={(e) => handleAnswerChange('standardsRevisionFrequency', e.target.value)}
                  rows="2"
                />
              </div>
            </div>

            <div className="question-group">
              <h3 className="question-group-title">5. Shitsuke (Sustain)</h3>
              
              <div className="question-item">
                <label htmlFor="fiveSCulture">
                  Are 5S practices part of the company culture or only applied occasionally?
                </label>
                <textarea 
                  id="fiveSCulture" 
                  value={answers.fiveSCulture} 
                  onChange={(e) => handleAnswerChange('fiveSCulture', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="internalAudits">
                  Are internal 5S audits carried out regularly? By whom?
                </label>
                <textarea 
                  id="internalAudits" 
                  value={answers.internalAudits} 
                  onChange={(e) => handleAnswerChange('internalAudits', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="deviationManagement">
                  How are 5S deviations handled and communicated to the team?
                </label>
                <textarea 
                  id="deviationManagement" 
                  value={answers.deviationManagement} 
                  onChange={(e) => handleAnswerChange('deviationManagement', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 2: Future Desired State - 5S Vision and Objectives</h2>
            
            <div className="question-group">
              <h3 className="question-group-title">Vision and ambition</h3>
              
              <div className="question-item">
                <label htmlFor="idealVision">
                  What is your ideal vision of a successful 5S environment?
                </label>
                <textarea 
                  id="idealVision" 
                  value={answers.idealVision} 
                  onChange={(e) => handleAnswerChange('idealVision', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="objectives">
                  What quantitative or qualitative objectives do you aim to achieve with 5S (ex: reduced search time, improved safety, fewer accidents)?
                </label>
                <textarea 
                  id="objectives" 
                  value={answers.objectives} 
                  onChange={(e) => handleAnswerChange('objectives', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="extensionToOtherDepartments">
                  Do you intend to extend the 5S approach to other departments (offices, maintenance, logistics, etc.)?
                </label>
                <textarea 
                  id="extensionToOtherDepartments" 
                  value={answers.extensionToOtherDepartments} 
                  onChange={(e) => handleAnswerChange('extensionToOtherDepartments', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="expectedBehaviorChanges">
                  What behavior changes do you expect from your teams with better 5S adoption?
                </label>
                <textarea 
                  id="expectedBehaviorChanges" 
                  value={answers.expectedBehaviorChanges} 
                  onChange={(e) => handleAnswerChange('expectedBehaviorChanges', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="digitalTools">
                  Are you planning to use digital tools to support or automate 5S activities (digital checklists, QR codes, audit apps)?
                </label>
                <textarea 
                  id="digitalTools" 
                  value={answers.digitalTools} 
                  onChange={(e) => handleAnswerChange('digitalTools', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="questionnaire-step">
            <h2 className="step-title">Part 3: Gap Analysis, Monitoring, and Change Management</h2>
            
            <div className="question-group">
              <h3 className="question-group-title">Deployment management</h3>
              
              <div className="question-item">
                <label htmlFor="projectManager">
                  Who is responsible for managing and monitoring the 5S project?
                </label>
                <textarea 
                  id="projectManager" 
                  value={answers.projectManager} 
                  onChange={(e) => handleAnswerChange('projectManager', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="performanceIndicators">
                  How do you track improvements over time? Have you implemented performance indicators?
                </label>
                <textarea 
                  id="performanceIndicators" 
                  value={answers.performanceIndicators} 
                  onChange={(e) => handleAnswerChange('performanceIndicators', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="mainDifficulties">
                  What are the main difficulties encountered in sustainably applying 5S?
                </label>
                <textarea 
                  id="mainDifficulties" 
                  value={answers.mainDifficulties} 
                  onChange={(e) => handleAnswerChange('mainDifficulties', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="trainingActions">
                  What training or awareness actions have you implemented or planned?
                </label>
                <textarea 
                  id="trainingActions" 
                  value={answers.trainingActions} 
                  onChange={(e) => handleAnswerChange('trainingActions', e.target.value)}
                  rows="3"
                />
              </div>

              <div className="question-item">
                <label htmlFor="bestPractices">
                  What success stories or best practices have you already observed that you want to generalize across the company?
                </label>
                <textarea 
                  id="bestPractices" 
                  value={answers.bestPractices} 
                  onChange={(e) => handleAnswerChange('bestPractices', e.target.value)}
                  rows="3"
                />
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const element = document.querySelector(".x5s1-page");
      if (!element) {
        alert("âŒ Element not found!");
        return;
      }

      document.querySelectorAll("*").forEach((el) => {
        const bgImage = window.getComputedStyle(el).backgroundImage;
        if (bgImage && bgImage.includes("gradient")) {
          el.style.backgroundImage = "none";
        }

        const bgColor = window.getComputedStyle(el).backgroundColor;
        if (
          bgColor.includes("var(") ||
          bgColor === "transparent" ||
          bgColor === "rgba(0, 0, 0, 0)"
        ) {
          el.style.backgroundColor = "#ffffff";
        }
      });

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight;
      }

      const date = new Date().toISOString().slice(0, 10);
      pdf.save(`5s_${date}.pdf`);
      alert("âœ… PDF successfully generated!");
    } catch (err) {
      console.error("PDF Error:", err);
      alert("âŒ PDF Error: " + (err.message || "unknown"));
    }
  };

  const saveLeanAnswers = async (moduleName) => {
    try {
      const caseId = localStorage.getItem("caseId");
      if (!caseId) {
        alert("No active case found!");
        return;
      }

      console.log(`Saving answers for ${moduleName} in case ${caseId}`);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/lean/${moduleName}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          case_id: caseId,
          answers: answers,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || `Save error (${response.status}): ${data.message}`);
      }

      alert(`âœ… ${moduleName.toUpperCase()} answers saved successfully!`);

      handleNextStep();

    } catch (error) {
      console.error("âŒ saveLeanAnswers Error:", error);
      alert("âŒ " + error.message);
    }
  };

  return (
    <div className="container-center-horizontal">
      <div className="x5s1-page screen">
        <div className="header">
          <Link to="/home">
            <img
              className="logo-icam"
              src="/img/1200px-logo-icam---2008-1@2x.png"
              alt="ICAM Logo"
            />
          </Link>
        </div>
        
        <div className="questionnaire-container">
          <h1 className="question-title">5S Methodology</h1>
          
          {!showQuestionnaire ? (
            <div className="question-section">
              <p className="question-text">
                Has your company already implemented the 5S methodology?
              </p>
              
              <div className="buttons-container">
                <button 
                  className={`choice-button ${hasImplemented ? 'active' : ''}`}
                  onClick={handleYesClick}
                >
                  Yes
                </button>
                <button 
                  className={`choice-button ${!hasImplemented ? 'active' : ''}`}
                  onClick={() => setHasImplemented(false)}
                >
                  No
                </button>
              </div>

              {!hasImplemented && (
                <div className="redirect-section">
                  <p className="redirect-text">
                    We recommend starting by understanding the basic Lean principles:
                  </p>
                  <Link to="/Kaizen-1" className="redirect-button">
                    Go to the Kaizen Method
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <>
              {!showImport ? (
                <div className="questionnaire-container-inner" ref={questionnaireRef}>
                  {renderProgressIndicator()}
                  {renderQuestionnaire()}
                  
                  <div className="navigation-buttons">
                    {currentStep > 1 && (
                      <button 
                        className="nav-button prev-button"
                        onClick={handlePrevStep}
                      >
                        Previous
                      </button>
                    )}
                    
                    <button 
                      className="nav-button next-button"
                      onClick={() => saveLeanAnswers("5s")}
                    >
                      {currentStep < 3 ? "Next" : "Finish"}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="file-upload-section">
                  <h2 className="upload-title">Import your existing documents</h2>
                  <p className="upload-description">
                    You can now import your existing 5S documents, checklists, or photos:
                  </p>
                  
                  <div className="upload-container">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden-file-input"
                      accept=".pdf,.jpg,.jpeg,.png,.svg,.xlsx,.xls,.doc,.docx"
                      onChange={handleFileUpload}
                      disabled={uploadStatus.isUploading}
                    />
                    
                    <button 
                      className="import-button"
                      onClick={handleImportClick}
                      disabled={uploadStatus.isUploading}
                    >
                      {uploadStatus.isUploading ? 'Uploading...' : 'Import a file'}
                    </button>
                    
                    {uploadStatus.isUploading && (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div 
                            className="progress-bar-fill" 
                            style={{ width: `${uploadStatus.progress}%` }}
                          ></div>
                        </div>
                        <p className="progress-text">Uploading... {uploadStatus.progress}%</p>
                      </div>
                    )}

                    {uploadStatus.error && (
                      <div className="upload-error">
                        <p>{uploadStatus.error}</p>
                      </div>
                    )}

                    {uploadStatus.success && (
                      <div className="upload-success">
                        <p>The file {uploadStatus.fileName} was successfully uploaded!</p>
                        {uploadStatus.fileId && (
                          <button 
                            className="download-button"
                            onClick={() => window.open(`/api/5s/${uploadStatus.fileId}`, '_blank')}
                          >
                            View file
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="navigation-buttons">
                    <button 
                      className="nav-button prev-button"
                      onClick={() => setShowImport(false)}
                    >
                      Back to questionnaire
                    </button>
                    
                    <Link to="/5s-2" className="nav-button next-button">
                      Continue
                    </Link>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <button 
          className="download-button"
          onClick={handleDownloadPDF}
          disabled={uploadStatus.isUploading}
        >
          {uploadStatus.isUploading ? "Generating PDF..." : "Download PDF"}
        </button>
      </div>
    </div>
  );
}

export default X5S1;
